import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "services";
import _intersection from "lodash/intersection";
// import _difference from "lodash/difference";
import _isEmpty from "lodash/isEmpty";
import _uniq from "lodash/uniq";
import { queryStringFromArray } from "helpers";

const collectionDropdownAdapter = createEntityAdapter();
const initialState = collectionDropdownAdapter.getInitialState({
  fetchAllStatus: "idle",
  fetchAllErrorMessage: "",
  addAssetsStatus: "idle",
  addAssetsErrorMessage: "",
  removeAssetsStatus: "idle",
  removeAssetsErrorMessage: "",
  data: [],
  hasNextPage: false,
  page: 0,
  pageSize: 50,
  sortBy: "name",
  orderBy: "asc",
  searchTerm: "",
  add: {
    collectionId: [],
    assetId: [],
  },
  remove: {
    collectionId: [],
    assetId: [],
  },
  submit: false,
});

export const fetchAllCollections = createAsyncThunk(
  "collectionDropdown/fetchAllCollections",
  async ({ entityId, mapId }, { rejectWithValue, getState }) => {
    const { page, pageSize, sortBy, orderBy, searchTerm } =
      getState().collectionDropdown;
    const query = `/assets/collections?entityId=${entityId}&cartographyId=${mapId}&page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const addAssets = createAsyncThunk(
  "collectionDropdown/addAssets",
  async ({ entityId, assetId, collectionId }, { rejectWithValue }) => {
    const qsAssetId = queryStringFromArray(assetId, "assetId");
    const qsCollectionId = queryStringFromArray(collectionId, "collectionId");
    const query = `/entities/${entityId}/assets/collections/add?${qsCollectionId}&${qsAssetId}`;
    try {
      const response = await uncoveryApi.put(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const removeAssets = createAsyncThunk(
  "collectionDropdown/removeAssets",
  async ({ entityId, assetId, collectionId }, { rejectWithValue }) => {
    const qsAssetId = queryStringFromArray(assetId, "assetId");
    const qsCollectionId = queryStringFromArray(collectionId, "collectionId");
    const query = `/entities/${entityId}/assets/collections/remove?${qsCollectionId}&${qsAssetId}`;
    try {
      const response = await uncoveryApi.put(query);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const collectionDropdownSlice = createSlice({
  name: "collectionDropdown",
  initialState,
  reducers: {
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 0;
      state.searchTerm = searchTerm;
      return state;
    },
    sort: (state, { payload }) => {
      const { sortBy } = payload;
      state.page = 0;
      if (sortBy !== state.sortBy) {
        state.orderBy = "asc";
        state.sortBy = sortBy;
      } else {
        state.orderBy = state.orderBy === "desc" ? "asc" : "desc";
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    initDropdown: (state, { payload }) => {
      const { assetsSelected } = payload;
      state.data = state.data.map((collection) => {
        const intersectionLength = _intersection(
          assetsSelected,
          collection.assets.map((ca) => ca.id)
        ).length;
        const condition = assetsSelected.length > intersectionLength;
        let current = "";

        if (intersectionLength && !condition) current = "checked";
        else if (intersectionLength && condition) current = "combined";
        const test = {
          ...collection,
          initial: current,
          current: current,
        };
        return test;
      });
      return state;
    },
    updateInitial: (state, { payload }) => {
      const { collectionId, initial } = payload;
      const index = state.data.findIndex(
        (collection) => collection.id === collectionId
      );
      state.data[index].initial = initial;
      return state;
    },
    updateCurrent: (state, { payload }) => {
      const { collectionId, current } = payload;
      const index = state.data.findIndex(
        (collection) => collection.id === collectionId
      );
      state.data[index].current = current;
      return state;
    },
    updateAssets: (state, { payload }) => {
      const {
        assetId,
        collectionId,
        action,
        destination,
        // combined = false,
      } = payload;
      if (action === "add") {
        let _assetId = assetId;
        // if (combined) {
        //   const index = state.data.findIndex(
        //     (collection) => collection.id === collectionId
        //   );
        //   if (destination === "add") {
        //     const difference = _difference(
        //       assetId,
        //       state.data[index].assets.map((ca) => ca.id)
        //     );
        //     _assetId = difference;
        //   } else {
        //     const intersection = _intersection(
        //       assetId,
        //       state.data[index].assets.map((ca) => ca.id)
        //     );
        //     _assetId = intersection;
        //   }
        // }
        state[destination].assetId = _uniq([
          ...state[destination].assetId,
          ..._assetId,
        ]);
        state[destination].collectionId = _uniq([
          ...state[destination].collectionId,
          collectionId,
        ]);
      } else if (action === "remove") {
        state[destination].collectionId = state[
          destination
        ].collectionId.filter((c) => c !== collectionId);

        if (!state[destination].collectionId.length) {
          state[destination].assetId = [];
        }
      }
      // Update submit (detect changes)
      if (
        _isEmpty(state.add.collectionId) &&
        _isEmpty(state.remove.collectionId)
      ) {
        state.submit = false;
      } else {
        state.submit = true;
      }
      return state;
    },
    resetAddRemove: (state) => {
      state.add.collectionId = [];
      state.add.assetId = [];
      state.remove.collectionId = [];
      state.remove.assetId = [];
      state.submit = false;
    },
    removeOneCollectionDropdown: (state, { payload }) => {
      const { collectionId } = payload;
      state.data = state.data.filter((c) => c.id !== collectionId);
      return state;
    },
    addOneCollectionDropdown: (state, { payload }) => {
      const { collection } = payload;
      state.data = [collection].concat(state.data);
      return state;
    },
    updateOneCollectionDropdown: (state, { payload }) => {
      const { collection } = payload;
      const index = state.data.findIndex((c) => c.id === collection.id);
      state.data[index] = collection;
      return state;
    },
    resetCollectionDropdownState: () => initialState,
  },
  extraReducers: {
    [fetchAllCollections.pending]: (state, { payload }) => {
      state.fetchAllStatus = "loading";
    },
    [fetchAllCollections.fulfilled]: (state, { payload }) => {
      if (state.page === 0) {
        state.data = payload.data;
      } else {
        state.data = [...state.data, ...payload.data];
      }
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.fetchAllStatus = "success";
    },
    [fetchAllCollections.rejected]: (state, { payload }) => {
      state.fetchAllErrorMessage = payload.message;
      state.fetchAllStatus = "error";
    },
    [addAssets.pending]: (state, { payload }) => {
      state.addAssetsStatus = "loading";
    },
    [addAssets.fulfilled]: (state, { payload, meta }) => {
      const { collectionId, assetId } = meta.arg;
      collectionId.forEach((id) => {
        const index = state.data.findIndex((c) => c.id === id);
        state.data[index].assets = [
          ...state.data[index].assets,
          ...assetId.map((c) => {
            return { id: c };
          }),
        ];
      });
      state.add.collectionId = [];
      state.add.assetId = [];
      state.submit = false;
      state.addAssetsStatus = "success";
    },
    [addAssets.rejected]: (state, { payload }) => {
      state.addAssetsErrorMessage = payload.message;
      state.addAssetsStatus = "error";
    },
    [removeAssets.pending]: (state, { payload }) => {
      state.removeAssetsStatus = "loading";
    },
    [removeAssets.fulfilled]: (state, { payload, meta }) => {
      const { collectionId, assetId } = meta.arg;
      collectionId.forEach((id) => {
        const index = state.data.findIndex((c) => c.id === id);
        state.data[index].assets = state.data[index].assets.filter(
          (a) => !assetId.includes(a.id)
        );
      });
      state.remove.collectionId = [];
      state.remove.assetId = [];
      state.submit = false;
      state.removeAssetsStatus = "success";
    },
    [removeAssets.rejected]: (state, { payload }) => {
      state.removeAssetsErrorMessage = payload.message;
      state.removeAssetsStatus = "error";
    },
  },
});

export const {
  updateSearchTerm,
  sort,
  updatePage,
  resetCollectionDropdownState,
  updateAssets,
  updateInitial,
  updateCurrent,
  initDropdown,
  resetAddRemove,
  removeOneCollectionDropdown,
  addOneCollectionDropdown,
  updateOneCollectionDropdown,
} = collectionDropdownSlice.actions;

export default collectionDropdownSlice.reducer;

const collectionDropdownState = (state) => state.collectionDropdown;

export const selectCollectionIds = createSelector(
  collectionDropdownState,
  (collections) => collections.data.map((collection) => collection.id)
);
export const selectCollections = createSelector(
  collectionDropdownState,
  (collections) => collections.data
);
export const selectCollectionById = createSelector(
  [
    collectionDropdownState,
    (collectionDropdownState, collectionId) => collectionId,
  ],
  (collections, collectionId) =>
    collections.data.find((collection) => collection.id === collectionId)
);
